/**
 * reconcileApi — Zahlungsabgleich über atomare DB-Funktionen
 * (supabase/migrations/20261017_01_fibu_abgleich.sql).
 */
import { SupabaseAdapter } from '@/api/adapters/SupabaseAdapter';
import { isMissingRpc } from '@/lib/invoiceApi';

const COMPANY_ID = 1;

function client() {
  const c = SupabaseAdapter.getClient();
  if (!c) throw new Error('Keine Verbindung zur Datenbank.');
  return c;
}

async function rpc(name, args) {
  const { data, error } = await client().rpc(name, args);
  if (error) {
    const missing = isMissingRpc(error);
    const err = new Error(missing
      ? 'Abgleich noch nicht eingerichtet — bitte Migration 20261017_01_fibu_abgleich.sql in Supabase ausführen.'
      : (error.message || 'Vorgang fehlgeschlagen.'));
    err.code = missing ? 'RPC_FEHLT' : error.code;
    err.hint = error.hint;
    throw err;
  }
  return data;
}

const newKey = () => (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);

/** Status-Reiter → DB-Status */
const TAB_STATUS = {
  offen: ['unmatched', 'partial'],
  gebucht: ['matched', 'ignored'],
  geloescht: ['deleted'],
};

/**
 * Umsätze für die Transaktionsliste.
 * @param {{ tab?: string, connectionId?: number|null, from?: string, to?: string }} f
 */
export async function listTransactions({ tab = 'offen', connectionId = null, from = null, to = null } = {}) {
  let q = client().from('fibu_financial_transactions')
    .select('*, fibu_financial_connections!inner(company_id, display_name, provider, linked_account_no)')
    .eq('fibu_financial_connections.company_id', COMPANY_ID)
    .order('booking_date', { ascending: false })
    .order('id', { ascending: false })
    .limit(500);
  if (TAB_STATUS[tab]) q = q.in('reconciliation_status', TAB_STATUS[tab]);
  if (connectionId) q = q.eq('connection_id', connectionId);
  if (from) q = q.gte('booking_date', from);
  if (to) q = q.lte('booking_date', to);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

/** Anzahl je Reiter (für die Zähler) */
export async function countTransactions({ connectionId = null } = {}) {
  const out = {};
  await Promise.all(Object.entries(TAB_STATUS).map(async ([tab, statuses]) => {
    let q = client().from('fibu_financial_transactions')
      .select('id, fibu_financial_connections!inner(company_id)', { count: 'exact', head: true })
      .eq('fibu_financial_connections.company_id', COMPANY_ID)
      .in('reconciliation_status', statuses);
    if (connectionId) q = q.eq('connection_id', connectionId);
    const { count, error } = await q;
    if (error) throw error;
    out[tab] = count || 0;
  }));
  return out;
}

/** Zuordnungen eines Umsatzes (für „Gebucht“-Details) */
export async function listTransactionAllocations(txId) {
  const { data, error } = await client().from('fibu_reconciliation_parts')
    .select('amount, part, account_no, open_item_id, fibu_reconciliations!inner(id, status, category, booking_date, created_by, auto), fibu_open_items(invoice_no, partner_name)')
    .eq('transaction_id', txId).eq('part', 'tx');
  if (error) throw error;
  const recIds = (data || []).filter((p) => p.fibu_reconciliations.status === 'active').map((p) => p.fibu_reconciliations.id);
  if (!recIds.length) return [];
  const { data: parts, error: e2 } = await client().from('fibu_reconciliation_parts')
    .select('reconciliation_id, part, amount, account_no, fibu_open_items(invoice_no, partner_name)')
    .in('reconciliation_id', recIds);
  if (e2) throw e2;
  return (data || []).filter((p) => p.fibu_reconciliations.status === 'active').map((p) => ({
    ...p.fibu_reconciliations,
    amount: p.amount,
    parts: (parts || []).filter((x) => x.reconciliation_id === p.fibu_reconciliations.id && x.part !== 'tx'),
  }));
}

/**
 * Zuordnen. payload: { mode:'items', items:[{open_item_id, amount}], difference?:{reason} }
 *               oder { mode:'account', category, interest?, account_no?, note? }
 */
export const reconcile = (txId, payload, actor) =>
  rpc('fibu_reconcile', { p_tx_id: txId, p_payload: payload, p_idempotency_key: newKey(), p_actor: actor || null });

export const unreconcile = (txId, reason, actor) =>
  rpc('fibu_unreconcile_tx', { p_tx_id: txId, p_reason: reason || null, p_actor: actor || null });

export const deleteTransaction = (txId, reason, actor) =>
  rpc('fibu_tx_delete', { p_tx_id: txId, p_reason: reason, p_actor: actor || null });

export const restoreTransaction = (txId, actor) =>
  rpc('fibu_tx_restore', { p_tx_id: txId, p_actor: actor || null });

export const reconcileStats = (from, to) => rpc('fibu_reconcile_stats', { p_from: from, p_to: to });

// ── Buchungskonten für „ohne Beleg“ (fibu_settings.accounts) ──

export const ACCOUNT_SETTING_FIELDS = [
  { key: 'gesellschafter', label: 'Privatentnahme / Gesellschafter-Verrechnungskonto', hint: 'Bei der UG mit dem Steuerberater festlegen.' },
  { key: 'kredit', label: 'Kredit (Tilgung)', hint: 'Standard 3150' },
  { key: 'kredit_zinsen', label: 'Kreditzinsen', hint: 'Standard 7320' },
  { key: 'geldtransit', label: 'Geldtransit', hint: 'Standard 1460' },
  { key: 'zahlungsverkehr', label: 'Kosten des Zahlungsverkehrs', hint: 'Standard 6855' },
];

export async function loadAccountSettings() {
  const { data, error } = await client().from('fibu_settings').select('accounts').eq('company_id', COMPANY_ID).maybeSingle();
  if (error) throw error;
  return data?.accounts || {};
}

/** Speichert nur existierende SKR04-Konten; leere Felder = Standard bzw. nicht festgelegt */
export async function saveAccountSettings(values, actor) {
  const cleaned = {};
  const wanted = [];
  ACCOUNT_SETTING_FIELDS.forEach(({ key }) => {
    const v = String(values[key] ?? '').trim();
    if (v && !/^\d{4,8}$/.test(v)) throw new Error(`Ungültige Kontonummer: ${v}`);
    cleaned[key] = v || null;
    if (v) wanted.push(v);
  });
  if (wanted.length) {
    const { data, error } = await client().from('fibu_accounts').select('account_no').in('account_no', wanted);
    if (error) throw error;
    const known = new Set((data || []).map((a) => a.account_no));
    const missing = wanted.filter((a) => !known.has(a));
    if (missing.length) throw new Error(`Konto nicht im Kontenrahmen: ${missing.join(', ')}`);
  }
  const current = await loadAccountSettings();
  const { error } = await client().from('fibu_settings').upsert({
    company_id: COMPANY_ID, accounts: { ...current, ...cleaned }, updated_at: new Date().toISOString(), updated_by: actor || null,
  }, { onConflict: 'company_id' });
  if (error) throw error;
}
